import { Link, router } from 'expo-router';
import * as React from 'react';
import { Pressable } from 'react-native';
import {
    BodyMediumRegular,
    ChevronRightIcon,
    FootnoteRegular,
    HandshakeIcon,
    Icon,
    type IconType,
    KeyIcon,
    MailIcon,
    Separator,
    Sheet,
    View,
    XStack,
    YStack,
} from 'smoothie';

import { useSendEmailToSupport } from '../../../common/hooks/useSendEmailToSupport';
import { SUPPORT_EMAIL } from '../../../settings/types/constants';

interface LoginHelpSheetItemProps {
    title: string | React.ReactNode;
    onPress?: () => void;
    icon: IconType;
    rightIcon?: IconType;
}

const LoginHelpSheetItem = ({
    title,
    onPress,
    icon,
    rightIcon,
}: LoginHelpSheetItemProps) => {
    return (
        <Pressable
            onPress={onPress}
            style={({ pressed }) => [
                {
                    opacity: pressed ? 0.6 : 1,
                    transform: pressed ? [{ scale: 0.99 }] : [{ scale: 1 }],
                },
            ]}
            tabIndex={-1}
        >
            <XStack
                gap={10}
                alignItems="center"
                justifyContent="space-between"
                paddingVertical={24}
                paddingHorizontal={8}
            >
                <XStack alignItems="center" gap={16} maxWidth="90%">
                    <Icon icon={icon} color="$gray1200" />
                    <BodyMediumRegular>{title}</BodyMediumRegular>
                </XStack>

                {rightIcon !== undefined ? (
                    <LineItemIcon icon={rightIcon} />
                ) : (
                    <ChevronIcon />
                )}
            </XStack>
        </Pressable>
    );
};

const LineItemIcon = ({ icon }: { icon: IconType }) => {
    return <Icon icon={icon} color="$gray1200" size={16} />;
};

export const ChevronIcon = () => {
    return <LineItemIcon icon={ChevronRightIcon} />;
};

interface LoginHelpSheetProps {
    isOpen: boolean;
    onClose: () => void;
}

const LoginHelpSheet = ({ isOpen, onClose }: LoginHelpSheetProps) => {
    const sendEmailToSupport = useSendEmailToSupport();
    const mailSubject = 'Trouble signing in';
    const mailtoHref = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(
        mailSubject,
    )}`;
    const helpSheetItems: LoginHelpSheetItemProps[] = [
        {
            title: 'Forgot password',
            onPress: () => {
                onClose();
                router.navigate('/sign-in/forgot-password');
            },
            icon: KeyIcon,
        },
        {
            title: (
                <YStack>
                    <BodyMediumRegular>
                        Contact Fruitful Hospitality
                    </BodyMediumRegular>
                    <Link
                        href={mailtoHref}
                        onPress={(e) => {
                            e?.stopPropagation();
                        }}
                    >
                        <FootnoteRegular color="$gray1000">
                            {SUPPORT_EMAIL}
                        </FootnoteRegular>
                    </Link>
                </YStack>
            ),
            onPress: () => {
                onClose();
                void sendEmailToSupport({ subject: mailSubject });
            },
            icon: HandshakeIcon,
            rightIcon: MailIcon,
        },
    ];

    return (
        <Sheet
            isOpen={isOpen}
            onClose={onClose}
            title="Trouble signing in?"
            showCloseButton
            forceKeyboardCompatibleBottomSheet
        >
            <YStack $md={{ minWidth: 400 }}>
                {helpSheetItems.map((item, index) => {
                    return (
                        <View key={index}>
                            <LoginHelpSheetItem {...item} />
                            {index < helpSheetItems.length - 1 && <Separator />}
                        </View>
                    );
                })}
            </YStack>
        </Sheet>
    );
};

export default LoginHelpSheet;
